/**
 * DOM event handling for Annotation
 */

import type { AppState, Position } from '../types';
import type { Store } from '../store';
import type { EventBus } from '../event-bus';
import type { EventMap } from '../types';
import { collectElementInfo } from '../element';
import { setPassthroughMode } from './cursor';

/** Data attributes used by Annotation */
const DATA_TOOLBAR = 'data-annotation-toolbar';
const DATA_MARKER = 'data-annotation-marker';
const DATA_POPUP = 'data-annotation-popup';
const DATA_SETTINGS = 'data-annotation-settings';

/**
 * Check if an element is part of the Annotation UI
 */
export function isAnnotationElement(element: Element | null): boolean {
  if (!element) return false;

  // Check for the custom element itself (clicks on shadow DOM elements appear as the host)
  if (element.tagName.toLowerCase() === 'agent-ui-annotation') {
    return true;
  }

  // Check for the closest agent-ui-annotation ancestor
  if (element.closest('agent-ui-annotation')) {
    return true;
  }

  // Check for data attributes (for non-shadow-dom scenarios)
  return (
    element.hasAttribute(DATA_TOOLBAR) ||
    element.hasAttribute(DATA_MARKER) ||
    element.hasAttribute(DATA_POPUP) ||
    element.hasAttribute(DATA_SETTINGS) ||
    element.closest(`[${DATA_TOOLBAR}], [${DATA_MARKER}], [${DATA_POPUP}], [${DATA_SETTINGS}]`) !== null
  );
}

/**
 * Check if an event originated from within Annotation UI (including shadow DOM)
 */
export function isAnnotationEvent(event: Event): boolean {
  const hasComposedPath = typeof event.composedPath === 'function';
  if (hasComposedPath) {
    const path = event.composedPath();
    for (const target of path) {
      if (target instanceof Element) {
        if (target.tagName.toLowerCase() === 'agent-ui-annotation') {
          return true;
        }
        if (target.hasAttribute && (
          target.hasAttribute(DATA_TOOLBAR) ||
          target.hasAttribute(DATA_MARKER) ||
          target.hasAttribute(DATA_POPUP) ||
          target.hasAttribute(DATA_SETTINGS)
        )) {
          return true;
        }
      }
    }
    return false;
  }

  const target = event.target as Element | null;
  return isAnnotationElement(target);
}

/**
 * Get the target element from a mouse event, excluding Annotation elements
 */
export function getTargetElement(event: MouseEvent): Element | null {
  const target = event.target as Element;

  if (isAnnotationElement(target)) {
    return null;
  }

  return target;
}

/**
 * Get current text selection
 */
export function getSelectedText(): string | null {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed) {
    return null;
  }

  const text = selection.toString().trim();
  return text.length > 0 ? text : null;
}

/**
 * Create DOM event handlers bound to store and event bus
 */
export function createEventHandlers(
  store: Store<AppState>,
  eventBus: EventBus<EventMap>
) {
  let isActive = false;

  /**
   * Handle Escape key for closing popups, canceling selection, or deactivating
   */
  const handleEscape = (state: AppState, event: KeyboardEvent): boolean => {
    if (event.key !== 'Escape') return false;

    if (state.popupVisible) {
      store.setState({ popupVisible: false, popupAnnotationId: null });
      event.preventDefault();
      return true;
    }

    if (state.isSelecting) {
      store.setState({ isSelecting: false, selectionRect: null });
      event.preventDefault();
      return true;
    }

    if (state.mode !== 'disabled') {
      eventBus.emit('deactivate', undefined as never);
      event.preventDefault();
      return true;
    }

    return false;
  };

  /**
   * Handle click events
   */
  const handleClick = (event: MouseEvent) => {
    const state = store.getState();

    if (state.mode === 'disabled') return;
    if (state.passthroughActive) return;

    // Check composedPath to properly detect clicks inside shadow DOM
    if (isAnnotationEvent(event)) return;

    // Block ALL clicks from reaching the page when blockInteractions is ON.
    // This runs in capture phase, preventing the event from reaching page elements.
    if (state.settings.blockInteractions) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Don't process element selection when settings panel is open
    if (state.settingsPanelVisible) return;

    const target = getTargetElement(event);
    if (!target) return;

    const includeForensic = state.settings.outputLevel === 'forensic';
    const elementInfo = collectElementInfo(target, includeForensic);

    // Store click position as document-absolute coordinates
    const clickX = event.clientX;
    const clickY = event.clientY + window.scrollY;

    eventBus.emit('element:click', {
      element: target,
      elementInfo,
      clickX,
      clickY
    });
  };

  /**
   * Handle mouse down for potential drag selection
   */
  const handleMouseDown = (event: MouseEvent) => {
    const state = store.getState();

    if (state.mode === 'disabled') return;
    if (state.passthroughActive) return;

    const target = event.target as Element;
    if (isAnnotationElement(target)) return;

    // Block mousedown from reaching the page when blockInteractions is ON
    if (state.settings.blockInteractions) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Only start multi-select drag in multi-select mode
    if (state.mode !== 'multi-select') return;

    const position: Position = {
      x: event.clientX,
      y: event.clientY,
    };

    eventBus.emit('multiselect:start', { position });
  };

  /**
   * Handle mouse move for drag selection
   */
  const handleMouseMove = (event: MouseEvent) => {
    const state = store.getState();
    if (state.passthroughActive) return;

    if (!state.isSelecting && state.selectionRect === null) return;

    if (state.isSelecting && state.selectionRect) {
      const rect = {
        startX: state.selectionRect.startX,
        startY: state.selectionRect.startY,
        endX: event.clientX,
        endY: event.clientY,
      };

      eventBus.emit('multiselect:update', { rect });
    }
  };

  /**
   * Handle mouse up to complete drag selection
   */
  const handleMouseUp = (event: MouseEvent) => {
    const state = store.getState();
    if (state.passthroughActive) return;

    // Block from reaching the page when blockInteractions is ON
    if (state.mode !== 'disabled' && state.settings.blockInteractions && !isAnnotationEvent(event)) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!state.isSelecting) return;

    // Find elements in selection rectangle
    const rect = state.selectionRect;
    if (!rect) return;

    const elements = findElementsInRect(rect);
    eventBus.emit('multiselect:end', { elements });
  };

  /**
   * Handle scroll events
   * NOTE: The annotation element also tracks scrollY via its own always-on listener
   * so that dot-mode markers move correctly even when the tool is deactivated.
   * This handler is the primary one when the tool is active.
   */
  const handleScroll = () => {
    store.setState({ scrollY: window.scrollY });
  };

  /**
   * Handle keyboard events (runs in capture phase to block before page handlers)
   */
  const handleKeyDown = (event: KeyboardEvent) => {
    const state = store.getState();

    // Check if event originated from annotation UI
    if (isAnnotationEvent(event)) {
      // Still allow Escape key to work for closing popups
      if (event.key === 'Escape') {
        handleEscape(state, event);
        event.stopPropagation();
        event.stopImmediatePropagation();
        return;
      }

      // Allow Enter/Shift+Enter from text inputs so popup textarea can handle:
      // - Enter submit
      // - Shift+Enter newline
      const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
      const isTextInput = path.some((node) => {
        if (node instanceof HTMLTextAreaElement) return true;
        if (node instanceof HTMLInputElement) {
          return !['button', 'checkbox', 'radio', 'submit', 'reset'].includes(node.type);
        }
        return false;
      });
      const shouldAllowTextareaEnter = isTextInput && event.key === 'Enter';

      // Only block annotation-origin keys while the annotation popup is open.
      // This keeps page shortcuts (e.g. Space/G) working when only the toolbar is open.
      if (state.popupVisible && !shouldAllowTextareaEnter) {
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
      return;
    }

    // Event passthrough: temporarily give control back to the page for interactions.
    // Only activate passthrough when there's nothing left to dismiss (no popup, no selection).
    if (state.mode !== 'disabled' && event.key === 'Escape') {
      if (!state.passthroughActive && !state.popupVisible && !state.isSelecting) {
        store.setState({ passthroughActive: true });
        setPassthroughMode(true);
        event.preventDefault();
        return;
      }
    }

    // Handle Escape for closing popups / canceling selection / deactivating
    handleEscape(state, event);

    // Only block page keyboard shortcuts while the annotation popup is open.
    if (state.mode !== 'disabled' && state.settings.blockInteractions && state.popupVisible) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
  };

  const handleKeyUp = (event: KeyboardEvent) => {
    // Release passthrough
    const state = store.getState();
    if (state.passthroughActive && (event.key === 'Escape')) {
      store.setState({ passthroughActive: false });
      setPassthroughMode(false);
    }

    // Prevent annotation UI key events from propagating only while popup is open
    if (isAnnotationEvent(event)) {
      if (state.popupVisible) {
        // Keep annotation UI key events from reaching page-level shortcuts.
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
      return;
    }

    if (state.mode !== 'disabled' && state.settings.blockInteractions && state.popupVisible) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
  };

  /**
   * Clean up passthrough when window loses focus (e.g. Cmd+Tab)
   */
  const handleWindowBlur = () => {
    const state = store.getState();
    if (state.passthroughActive) {
      store.setState({ passthroughActive: false });
      setPassthroughMode(false);
    }
  };

  /**
   * Attach event listeners
   */
  const attach = () => {
    if (isActive) return;

    document.addEventListener('click', handleClick, true);
    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('mousemove', handleMouseMove, true);
    document.addEventListener('mouseup', handleMouseUp, true);
    document.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('keyup', handleKeyUp, true);
    window.addEventListener('blur', handleWindowBlur);

    isActive = true;
  };

  /**
   * Detach event listeners
   */
  const detach = () => {
    if (!isActive) return;

    document.removeEventListener('click', handleClick, true);
    document.removeEventListener('mousedown', handleMouseDown, true);
    document.removeEventListener('mousemove', handleMouseMove, true);
    document.removeEventListener('mouseup', handleMouseUp, true);
    document.removeEventListener('scroll', handleScroll);
    document.removeEventListener('keydown', handleKeyDown, true);
    document.removeEventListener('keyup', handleKeyUp, true);
    window.removeEventListener('blur', handleWindowBlur);

    // Clean up passthrough state if it was active
    if (store.getState().passthroughActive) {
      store.setState({ passthroughActive: false });
      setPassthroughMode(false);
    }

    isActive = false;
  };

  return {
    attach,
    detach,
    isActive: () => isActive,
  };
}

/**
 * Find interactive elements within a selection rectangle
 */
function findElementsInRect(rect: { startX: number; startY: number; endX: number; endY: number }): Element[] {
  // Normalize rectangle
  const minX = Math.min(rect.startX, rect.endX);
  const maxX = Math.max(rect.startX, rect.endX);
  const minY = Math.min(rect.startY, rect.endY);
  const maxY = Math.max(rect.startY, rect.endY);

  // Query for interactive elements
  const selector = 'button, a, input, img, p, h1, h2, h3, h4, h5, h6, li, label, td, th';
  const candidates = document.querySelectorAll(selector);

  const viewport = {
    width: window.innerWidth,
    height: window.innerHeight,
  };

  const results: Element[] = [];

  for (const element of candidates) {
    // Skip Annotation elements
    if (isAnnotationElement(element)) continue;

    const bounds = element.getBoundingClientRect();

    // Skip elements that don't intersect selection
    if (
      bounds.right < minX ||
      bounds.left > maxX ||
      bounds.bottom < minY ||
      bounds.top > maxY
    ) {
      continue;
    }

    // Skip elements too large (> 80% viewport width AND > 50% height)
    if (bounds.width > viewport.width * 0.8 && bounds.height > viewport.height * 0.5) {
      continue;
    }

    // Skip elements too small (< 10x10)
    if (bounds.width < 10 || bounds.height < 10) {
      continue;
    }

    results.push(element);
  }

  // Filter out parent elements (keep only leaf nodes in selection)
  return results.filter((element) => {
    return !results.some(
      (other) => other !== element && element.contains(other)
    );
  });
}

export type EventHandlers = ReturnType<typeof createEventHandlers>;
